import angular from 'angular'

import { get, wrap, noop, compact, uniqueId } from 'lodash-es'
import { noargs, safeGet, defaultTo } from '../utils/bits'

import Pull from '../utils/pull'
import envRaw from '../utils/env-raw'
import deline from '../utils/deline'
import profile from '../utils/profile'
import StrictObj from '../utils/strict-obj'
import attachGetters from '../utils/attach-getters'
import attachSetters from '../utils/attach-setters'

import { getDiffPosts } from '../utils/posth'
import { keydownEsc, keydownLeft, keydownRight } from '../utils/keydown'
import { PostHash, buildPostHash, mergePostHash, flushPostHash, buildPostList, isEqualPostHash } from './gallery-modal-list.post-hash.js'

import Template from './gallery-modal-list.component.ejs'
import CLOSE_ICON from '../../img/modal-list-buttons/grey-close.svg'

angular
  .module('ggs.gallery-modal-list')
  .component('ggsGalleryModalList', {
    template: Template(StrictObj({
      LE: envRaw.liveEditMode,
      CLOSE_ICON
    })),
    controller: GalleryModalListComponent,
    bindings: {
      ggsPreload: '<',
      ggsStylerNew: '<',
      ggsProperties: '<',
      ggsGetPrevModal: '<',
      ggsGetNextModal: '<',
      ggsPostVoteEmitter: '<',
      ggsPostFlagEmitter: '<',
      ggsPopupOpenHandler: '<',
      ggsPopupClosedHandler: '<',
      ggsListAttachCallback: '<',
      ggsModalReadyHandler: '<?',
      ggsModalDisplayedHandler: '<',
      ggsVoteChangeHandler: '<',
      ggsVoteCountHideHandler: '<',
      ggsVotingDisallowHandler: '<'
    }
  })

const MAIN_QS = '.ggs-gml__main'

const SYNC_INTERVAL_DELAY = 2e3
const SYNC_THROTTLE_DELAY = 1e3
const CLICK_THROTTLE_DELAY = 0.3e3
const HOTKEY_THROTTLE_DELAY = 0.3e3

function GalleryModalListComponent(
  $portalElement, qh, $log, domh, $scope,
  $element, $interval, debugObj, LazyResources,
  waitAngular, StayInQueue, DefineHandler, DefineMethod,
  addIdPrefix, GmlView, GmlSite, GmlStyleHandlers) {

  const instanceId = uniqueId()
  const sign = `galleryList[uid=${instanceId}]`
  const $ctrl = $scope.$ctrl = debugObj(sign, attachGetters(attachSetters(StrictObj(this))))

  $ctrl.wrapM = DefineMethod(sign)
  $ctrl.wrapH = DefineHandler(sign)

  $portalElement.append($element)
  const listId = addIdPrefix(`gml-${instanceId}`)
  const listQs = `#${listId}`
  const listElm = domh.find($element, MAIN_QS)
  listElm.setAttribute('id', listId)

  $ctrl.isOpen = false

  const extModals = LazyResources(
    'hookPrev',
    'hookNext',
    'nthSlide',
    'lastSlide',
    'firstSlide',
    'applyState')

  const styleHandlers = GmlStyleHandlers(listQs)
  const stayInViewQueue = StayInQueue(`${sign}::view`)
  let intervalSyncId = null

  $ctrl.postHash = PostHash()
  $ctrl.postPull = $ctrl.ggsPreload && envRaw.modalListCanBePreloaded
    ? Pull(`${sign}::pull`, 3)
    : Pull(`${sign}::pull`)

  $ctrl.post2id = (post) =>
    `${listId}-${post.id}`

  $ctrl.wrapM('reportPopupOpen',
    noargs(wrap(listQs, $ctrl.ggsPopupOpenHandler)))

  $ctrl.wrapM('reportPopupClosed',
    noargs(wrap(listQs, $ctrl.ggsPopupClosedHandler)))

  $ctrl.wrapM('reportModalDisplayed',
    noargs(
      qh.makeFlow(
        $ctrl.getters.postHash.currPost,
        $ctrl.ggsModalDisplayedHandler)))

  const gmlView = GmlView(
    listElm,
    $ctrl.ggsProperties,
    $ctrl.postHash,
    $ctrl.post2id,
    $ctrl.reportPopupOpen,
    $ctrl.reportPopupClosed)

  const gmlSite = GmlSite(
    listElm,
    $ctrl.ggsProperties)

  // state

  $ctrl.wrapM('initState', (actCurr) => {
    return $ctrl.setupState(actCurr, true)
  })

  $ctrl.wrapM('fetchState', () => {
    return $ctrl.setupState($ctrl.postHash.currPost)
  })

  $ctrl.wrapM('setupState', (actCurr, skipSidePosts = false) => {
    const outHash = $ctrl.postHash

    const actPrev = skipSidePosts
      ? null
      : actCurr === outHash.nextPost
        ? outHash.currPost
        : $ctrl.ggsGetPrevModal(actCurr)

    const actNext = skipSidePosts
      ? null
      : actCurr === outHash.prevPost
        ? outHash.currPost
        : $ctrl.ggsGetNextModal(actCurr)

    const actList = compact([actPrev, actCurr, actNext])
    const actHash = buildPostHash(actCurr, actList)

    if (isEqualPostHash(outHash, actHash)) {
      return
    }

    const outList = buildPostList(outHash)
    const delPosts = getDiffPosts(outList, actList)
    const newPosts = getDiffPosts(actList, outList)

    $ctrl.postPull.release(delPosts)
    $ctrl.postPull.request(newPosts)

    mergePostHash($ctrl.postHash, actHash)
    $ctrl.publishState()

    return waitAngular()
  })

  $ctrl.wrapM('flushState', () => {
    flushPostHash($ctrl.postHash)
    $ctrl.postPull.releaseAll()
    $ctrl.publishState()
    return waitAngular()
  })

  $ctrl.wrapM('publishState', () => {
    $log.debug(deline(`
      ${sign}#publishState: {
        prevPost: ${get($ctrl.postHash, 'prevPost.id', '-')},
        currPost: ${get($ctrl.postHash, 'currPost.id', '-')},
        nextPost: ${get($ctrl.postHash, 'nextPost.id', '-')}
      }`))
    // we don't return this into the promise chain
    // because execution of `applyState` can take a while
    // and a result of execution will not affect this component
    extModals.applyState.mapParallel($ctrl.postHash).catch((err) => {
      $log.error(`${sign}#publishState: problem in background process:`, err)
    })
  })

  // ggsPostFlagEmitter handler

  $ctrl.wrapH('syncRequestHandler',
    qh.throttle(
      stayInViewQueue.make(
        qh.makeIf(
          $ctrl.getters.isOpen,
          $ctrl.fetchState)),
      SYNC_THROTTLE_DELAY))

  // <ggs-gallery-modal/> handlers

  $ctrl.wrapH('modalReadyHandler',
    qh.makeIf($ctrl.getters.isOpen, () => {
      const parentHandler = defaultTo(safeGet($ctrl, 'ggsModalReadyHandler'), noop)

      return qh.over(
        gmlView.refresh,
        parentHandler)
    }))

  $ctrl.wrapH('modalResizeHandler',
    qh.makeIf($ctrl.getters.isOpen, () => {
      return gmlView.refresh()
    }))

  $ctrl.wrapH('modalAttachHandler', (
    extModalHookPrev,
    extModalHookNext,
    extModalNthSlide,
    extModalLastSlide,
    extModalFirstSlide,
    extModalApplyState) => {

    extModals.add(
      extModalHookPrev,
      extModalHookNext,
      extModalNthSlide,
      extModalLastSlide,
      extModalFirstSlide,
      extModalApplyState)
    return extModalApplyState($ctrl.postHash)
  })

  $ctrl.wrapH('modalDetachHandler', (
    extModalHookPrev,
    extModalHookNext,
    extModalNthSlide,
    extModalLastSlide,
    extModalFirstSlide,
    extModalApplyState) => {

    extModals.del(
      extModalHookPrev,
      extModalHookNext,
      extModalNthSlide,
      extModalLastSlide,
      extModalFirstSlide,
      extModalApplyState)
  })

  // <ggs-gallery/> extList.open

  $ctrl.wrapM('extListOpen',
    stayInViewQueue.make(
      qh.makeIf(
        $ctrl.getters.negate.isOpen,
        qh.makeOver(
          $ctrl.setters.bindTrue.isOpen,
          $ctrl.initState,
          gmlView.open,
          $ctrl.fetchState,
          $ctrl.reportModalDisplayed))))

  // <ggs-gallery/> extList.close

  $ctrl.wrapM('close',
    stayInViewQueue.make(
      qh.makeIf(
        $ctrl.getters.isOpen,
        qh.makeOver(
          gmlView.close,
          $ctrl.flushState,
          $ctrl.setters.bindFalse.isOpen))))

  $ctrl.wrapM('extListClose', $ctrl.close)
  $ctrl.wrapH('bgCloseRequestHandler', $ctrl.close)
  $ctrl.wrapH('hotkeyCloseRequestHandler', $ctrl.close)
  $ctrl.wrapH('buttonCloseRequestHandler', $ctrl.close)

  // <ggs-gallery/> extList.prev & extList.next

  $ctrl.wrapM('switchTo', profile('switchTo',
    qh.makeIf(
      $ctrl.getters.isOpen,
      qh.makeOver(
        $ctrl.setupState,
        gmlView.switch,
        $ctrl.reportModalDisplayed))))

  $ctrl.wrapM('prev',
    stayInViewQueue.makeIfIdle(
      qh.makeIf(
        $ctrl.getters.postHash.prevPost,
        qh.makeFlow(
          $ctrl.getters.postHash.prevPost,
          $ctrl.switchTo))))

  $ctrl.wrapM('next',
    stayInViewQueue.makeIfIdle(
      qh.makeIf(
        $ctrl.getters.postHash.nextPost,
        qh.makeFlow(
          $ctrl.getters.postHash.nextPost,
          $ctrl.switchTo))))

  $ctrl.wrapM('extListPrev', $ctrl.prev)
  $ctrl.wrapM('extListNext', $ctrl.next)

  $ctrl.wrapH('bgPrevRequestHandler', qh.throttle($ctrl.prev, CLICK_THROTTLE_DELAY))
  $ctrl.wrapH('bgNextRequestHandler', qh.throttle($ctrl.next, CLICK_THROTTLE_DELAY))

  $ctrl.wrapH('modalPrevRequestHandler', $ctrl.prev)
  $ctrl.wrapH('modalNextRequestHandler', $ctrl.next)

  $ctrl.wrapH('hotkeyPrevRequestHandler',
    qh.throttle(
      qh.makeUnless(
        extModals.hookPrev.someSeries,
        qh.makeIf(
          $ctrl.getters.postHash.prevPost,
          qh.makeOver(
            $ctrl.prev,
            extModals.lastSlide.eachParallel))),
      HOTKEY_THROTTLE_DELAY))

  $ctrl.wrapH('hotkeyNextRequestHandler',
    qh.throttle(
      qh.makeUnless(
        extModals.hookNext.someSeries,
        qh.makeIf(
          $ctrl.getters.postHash.nextPost,
          qh.makeOver(
            $ctrl.next,
            extModals.firstSlide.eachParallel))),
      HOTKEY_THROTTLE_DELAY))

  // <ggs-gallery-modal-one/> extList.slide

  $ctrl.wrapM('extListSlide', extModals.nthSlide.eachParallel)

  // <ggs-gallery/> extList.isOpen & extList.isClosed

  $ctrl.wrapM('extListIsOpen', stayInViewQueue.make($ctrl.getters.isOpen))
  $ctrl.wrapM('extListIsClosed', stayInViewQueue.make($ctrl.getters.negate.isOpen))

  // $onInit

  $ctrl.wrapM('attachToStyler',
    qh.makeCatch(
      wrap(styleHandlers, $ctrl.ggsStylerNew.reg),
      wrap(`${sign}#attachToStyler: error halted:`, $log.error)))

  $ctrl.wrapM('attachToParent', () => {
    return $ctrl.ggsListAttachCallback(
      $ctrl.extListPrev,
      $ctrl.extListNext,
      $ctrl.extListOpen,
      $ctrl.extListClose,
      $ctrl.extListSlide,
      $ctrl.extListIsOpen,
      $ctrl.extListIsClosed)
  })

  $ctrl.wrapM('attachToEmitters',
    qh.makeJoin(
      wrap($ctrl.hotkeyCloseRequestHandler, keydownEsc.on),
      wrap($ctrl.hotkeyPrevRequestHandler, keydownLeft.on),
      wrap($ctrl.hotkeyNextRequestHandler, keydownRight.on),
      wrap($ctrl.syncRequestHandler, $ctrl.ggsPostFlagEmitter.on)))

  $ctrl.wrapM('setupSyncLoop', () => {
    intervalSyncId = $interval(
      $ctrl.syncRequestHandler,
      SYNC_INTERVAL_DELAY)
  })

  $ctrl.wrapH('$onInit',
    stayInViewQueue.make(
      qh.makeOver(
        $ctrl.attachToParent,
        $ctrl.attachToStyler,
        $ctrl.attachToEmitters,
        $ctrl.setupSyncLoop)))

  // $onDestroy

  $ctrl.wrapM('detachFromStyler',
    wrap(styleHandlers, $ctrl.ggsStylerNew.release))

  $ctrl.wrapM('detachFromEmitters',
    qh.makeJoin(
      wrap($ctrl.hotkeyCloseRequestHandler, keydownEsc.off),
      wrap($ctrl.hotkeyPrevRequestHandler, keydownLeft.off),
      wrap($ctrl.hotkeyNextRequestHandler, keydownRight.off),
      wrap($ctrl.syncRequestHandler, $ctrl.ggsPostFlagEmitter.off)))

  $ctrl.wrapM('cancelSyncLoop', () => {
    $interval.cancel(intervalSyncId)
  })

  $ctrl.wrapH('$onDestroy',
    qh.makeJoin(
      gmlView.destroy,
      gmlSite.destroy,
      $ctrl.detachFromStyler,
      $ctrl.detachFromEmitters,
      $ctrl.cancelSyncLoop,
      wrap($ctrl, debugObj.remove)))
}
